import React, { useEffect, useState } from "react";
import * as Pi from "@phosphor-icons/react";
import { CaretLeft, CaretRight, PlayCircle, GraduationCap, Lightbulb, ChatCircleDots, SpeakerHigh } from "@phosphor-icons/react";
import { PageLoader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";

const IconFor = (name) => Pi[name] || GraduationCap;

function isEmbed(url) {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url || "");
}
function embedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function Tutorial() {
  const [doc, setDoc] = useState(null);
  const [i, setI] = useState(0);

  useEffect(() => { api.get("/tutorial").then((r) => setDoc(r.data)).catch(() => setDoc({ steps: [] })); }, []);
  if (!doc) return <PageLoader />;

  const steps = doc.steps || [];
  const step = steps[i] || {};
  const StepIcon = IconFor(step.icon);
  const video = doc.video || {};
  const openHelp = () => window.dispatchEvent(new CustomEvent("eventra:open-chat", { detail: { subject: "Tutorial help", message: `I'm reading the "${step.title}" step in the tutorial. Can you explain it?` } }));

  return (
    <div className="max-w-6xl" data-testid="tutorial-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-transparent brand-gradient text-white shadow-lg shadow-primary/20 mb-8">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${video.poster || step.image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative p-8 md:p-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-3"><GraduationCap size={16} weight="fill" /> Organizer Guide</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">{doc.title || "How to Use the Platform"}</h1>
          <p className="text-white/80 mt-3 text-sm md:text-base">{doc.subtitle}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            <a href="#tutorial-video"><Button className="rounded-full bg-white text-[#7C3AED] hover:bg-white/90" data-testid="watch-video-btn"><PlayCircle size={18} weight="fill" className="mr-1.5" /> Watch tutorial video</Button></a>
            <Button variant="outline" onClick={openHelp} className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10" data-testid="tutorial-help-btn"><ChatCircleDots size={16} className="mr-1.5" /> Ask support</Button>
          </div>
        </div>
      </div>

      {/* Interactive steps */}
      <div className="grid lg:grid-cols-[260px_1fr] gap-6 mb-10">
        <div>
          <div className="flex items-center justify-between mb-2"><div className="overline">Steps</div><div className="text-xs text-muted-foreground">{i + 1}/{steps.length}</div></div>
          <div className="h-1.5 rounded-full bg-muted mb-3 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${steps.length ? ((i + 1) / steps.length) * 100 : 0}%` }} /></div>
          <div className="space-y-1 max-h-[520px] overflow-y-auto pr-1" data-testid="tutorial-steplist">
            {steps.map((s, idx) => {
              const SI = IconFor(s.icon);
              return (
                <button key={s.id || idx} onClick={() => setI(idx)} data-testid={`tutorial-step-${idx}`}
                  className={`w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm transition-colors ${i === idx ? "bg-foreground text-background" : "hover:bg-muted text-muted-foreground"}`}>
                  <span className={`grid place-items-center w-6 h-6 rounded-full text-[11px] font-bold shrink-0 ${i === idx ? "bg-primary text-primary-foreground" : "bg-muted-foreground/10"}`}>{idx + 1}</span>
                  <SI size={16} className="shrink-0" /> <span className="truncate">{s.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="tutorial-content">
          {step.image && <div className="h-52 md:h-64 bg-muted" style={{ backgroundImage: `url(${step.image})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-2 text-primary mb-2"><StepIcon size={22} weight="fill" /><span className="text-xs uppercase tracking-widest text-muted-foreground">Step {i + 1}</span></div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-3">{step.title}</h2>
            <p className="text-muted-foreground leading-relaxed">{step.body}</p>
            {(step.tips || []).length > 0 && (
              <div className="mt-5 space-y-2">
                {step.tips.map((t, k) => (
                  <div key={k} className="flex items-start gap-2 text-sm bg-primary/5 border border-primary/15 rounded-lg px-3 py-2">
                    <Lightbulb size={16} weight="fill" className="text-primary mt-0.5 shrink-0" /> <span>{t}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-7">
              <Button variant="outline" className="rounded-full" disabled={i === 0} onClick={() => setI((x) => Math.max(0, x - 1))} data-testid="tutorial-prev"><CaretLeft size={16} className="mr-1" /> Back</Button>
              <Button className="rounded-full" disabled={i >= steps.length - 1} onClick={() => setI((x) => Math.min(steps.length - 1, x + 1))} data-testid="tutorial-next">Next <CaretRight size={16} className="ml-1" /></Button>
            </div>
          </div>
        </div>
      </div>

      {/* Video */}
      <div id="tutorial-video" className="scroll-mt-24">
        <div className="overline mb-1">Walkthrough</div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight mb-4">Watch the tutorial video</h2>
        <div className="grid lg:grid-cols-[1fr_300px] gap-6">
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="tutorial-video-box">
            {video.url ? (
              isEmbed(video.url)
                ? <div className="aspect-video"><iframe title="Tutorial" src={embedUrl(video.url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                : <video controls poster={video.poster} className="w-full aspect-video bg-black" src={video.url} data-testid="tutorial-video-player" />
            ) : (
              <div className="aspect-video grid place-items-center bg-muted text-center p-6" style={video.poster ? { backgroundImage: `url(${video.poster})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                <div className="bg-background/85 backdrop-blur rounded-xl px-5 py-4">
                  <PlayCircle size={40} weight="fill" className="text-primary mx-auto mb-2" />
                  <p className="text-sm font-medium">The full walkthrough video is coming soon.</p>
                  <p className="text-xs text-muted-foreground">Follow the steps above in the meantime.</p>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold mb-2">Chapters</div>
            <div className="space-y-2" data-testid="tutorial-chapters">
              {(video.chapters || []).map((c, k) => (
                <div key={k} className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center gap-2 text-sm font-medium"><span className="text-primary">{String(k + 1).padStart(2, "0")}</span> {c.title}</div>
                  {c.audio_url && <audio controls src={c.audio_url} className="w-full mt-2 h-8" data-testid={`chapter-audio-${k}`} />}
                  {!c.audio_url && c.voiceover && <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex items-start gap-1"><SpeakerHigh size={12} className="mt-0.5" /> {c.voiceover}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
